import type { Level, Rect } from "../types";

export const BOARD = { w: 960, h: 640 };
export const CURSOR_RADIUS = 6;

/** Hard floor every level.minWidth must respect: cursor diameter + a small margin. */
const MIN_WIDTH_FLOOR = 2 * CURSOR_RADIUS + 4;

/** Straight axis-aligned corridor segment centered on the line (x1,y1)-(x2,y2). */
function seg(x1: number, y1: number, x2: number, y2: number, width: number): Rect {
  const half = width / 2;
  if (y1 === y2) {
    return { x: Math.min(x1, x2), y: y1 - half, w: Math.abs(x2 - x1), h: width };
  }
  if (x1 === x2) {
    return { x: x1 - half, y: Math.min(y1, y2), w: width, h: Math.abs(y2 - y1) };
  }
  throw new Error(`levels.seg: segment (${x1},${y1})-(${x2},${y2}) is not axis-aligned`);
}

/**
 * A connected chain of straight corridor segments through hand-authored waypoints.
 * `width` is either one number shared by every segment, or an array with exactly
 * `points.length - 1` entries (one per segment) — the latter is how a level bakes in a
 * deliberate, static "pinch": a segment narrower than its neighbours, independent of the
 * hunt mechanic's own dynamic narrowing (src/maze/hunt.ts), which only ever narrows a
 * corridor down to `level.minWidth` — never below it. A mismatched array length throws
 * at module load (a level-authoring mistake, not a runtime condition).
 */
function widthAt(width: number | readonly number[], i: number): number {
  // `typeof` (not `Array.isArray`) narrows reliably on both branches for a
  // `number | readonly number[]` union — `Array.isArray`'s `arg is any[]` predicate
  // fails to exclude `readonly number[]` from the negative (non-array) branch.
  if (typeof width === "number") return width;
  return width[i]!;
}

function corridorPath(
  points: ReadonlyArray<readonly [number, number]>,
  width: number | readonly number[],
): Rect[] {
  const segCount = points.length - 1;
  if (Array.isArray(width) && width.length !== segCount) {
    throw new Error(
      `levels.corridorPath: widths array has ${width.length} entries, expected ${segCount}`,
    );
  }

  const rects: Rect[] = [];
  for (let i = 0; i < segCount; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    rects.push(seg(a[0], a[1], b[0], b[1], widthAt(width, i)));
  }
  return rects;
}

function sliceStart(r: Rect, size: number): Rect {
  // A sub-rect at the "first" end of a corridor rect, used as the level start/goal zone.
  if (r.w >= r.h) {
    return { x: r.x, y: r.y, w: Math.min(size, r.w), h: r.h };
  }
  return { x: r.x, y: r.y, w: r.w, h: Math.min(size, r.h) };
}

function sliceEnd(r: Rect, size: number): Rect {
  if (r.w >= r.h) {
    const w = Math.min(size, r.w);
    return { x: r.x + r.w - w, y: r.y, w, h: r.h };
  }
  const h = Math.min(size, r.h);
  return { x: r.x, y: r.y + r.h - h, w: r.w, h };
}

interface LegDirection {
  readonly dx: -1 | 0 | 1;
  readonly dy: -1 | 0 | 1;
}

/** The final leg's travel direction, derived from the last two of a level's
 * hand-authored waypoints — the piece of information `sliceEnd`'s w>=h heuristic never
 * had, which is exactly what let it silently misplace a goal whose last leg travels
 * left or up (see `sliceFar`). */
function lastLegDirection(points: ReadonlyArray<readonly [number, number]>): LegDirection {
  const n = points.length;
  const a = points[n - 2]!;
  const b = points[n - 1]!;
  return { dx: Math.sign(b[0] - a[0]) as -1 | 0 | 1, dy: Math.sign(b[1] - a[1]) as -1 | 0 | 1 };
}

/**
 * A sub-rect at the TRUE far end of a corridor rect — the end the final leg's own
 * travel direction actually arrives at, not just "whichever end `sliceEnd`'s w>=h shape
 * heuristic happens to prefer". Rightward/downward travel reproduces `sliceEnd`'s
 * original numbers bit-for-bit (levels 1 and 2's final legs are both rightward, so
 * they're unaffected); leftward/upward travel places the goal at the opposite edge
 * instead — the fix for level 3, whose final leg travels upward, so its old goal sat at
 * the corridor's BOTTOM (its entry point from the previous leg) rather than its TOP
 * (the actual dead end), firing 'goal'/'win' the instant the walk entered that last
 * corridor. A degenerate (dx===dy===0) direction — never actually produced by
 * `corridorPath` — falls back to `sliceEnd`'s own heuristic.
 */
function sliceFar(r: Rect, size: number, dir: LegDirection): Rect {
  if (dir.dx !== 0) {
    const w = Math.min(size, r.w);
    return dir.dx > 0 ? { x: r.x + r.w - w, y: r.y, w, h: r.h } : { x: r.x, y: r.y, w, h: r.h };
  }
  if (dir.dy !== 0) {
    const h = Math.min(size, r.h);
    return dir.dy > 0 ? { x: r.x, y: r.y + r.h - h, w: r.w, h } : { x: r.x, y: r.y, w: r.w, h };
  }
  return sliceEnd(r, size);
}

// --- Level 1 "Meadow": a gentle teaching loop (~90px corridors) -------------
// Wide and forgiving (no static pinch — level 2 introduces that), but no longer just a
// two-turn Z: down, right, UP (the loop-back bend that teaches "look up to go up" for
// the first time), right, down, right — a wave-shaped path that's visually distinct
// from both switchback levels below while staying easy.
const LEVEL1_WIDTH = 90;
const level1Points = [
  [60, 100],
  [60, 320],
  [450, 320],
  [450, 120],
  [780, 120],
  [780, 500],
  [900, 500],
] as const;
const level1Corridors = corridorPath(level1Points, LEVEL1_WIDTH);

const LEVEL1: Level = {
  id: 1,
  name: "Wide Open",
  theme: "meadow",
  corridors: level1Corridors,
  start: sliceStart(level1Corridors[0]!, 60),
  goal: sliceFar(level1Corridors[level1Corridors.length - 1]!, 60, lastLegDirection(level1Points)),
  minWidth: 70,
};

// --- Level 2 "Dusk": switchbacks plus one deliberate static pinch ----------
// Same switchback waypoint shape as before, but corridors are wider by default (56px,
// leaving the dynamic hunt mechanic real room to bite) EXCEPT one segment — the middle
// vertical leg — which is a genuinely narrow, ALWAYS-tight bottleneck (32px) baked into
// the level itself, independent of focus/hunt. This is the level's first taste of a
// tight corridor, ahead of level 3's needle. minWidth (26) sits just under that pinch's
// own width, so the pinch still has a sliver of dynamic-hunt room rather than being
// already fully choked.
const LEVEL2_DEFAULT_WIDTH = 56;
const LEVEL2_PINCH_WIDTH = 32;
const LEVEL2_WIDTHS = [
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_PINCH_WIDTH, // the pinch: (470,60) -> (470,300)
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_DEFAULT_WIDTH,
  LEVEL2_DEFAULT_WIDTH,
] as const;
const level2Points = [
  [50, 60],
  [50, 260],
  [260, 260],
  [260, 60],
  [470, 60],
  [470, 300],
  [700, 300],
  [700, 560],
  [900, 560],
] as const;
const level2Corridors = corridorPath(level2Points, LEVEL2_WIDTHS);

const LEVEL2: Level = {
  id: 2,
  name: "Switchback",
  theme: "dusk",
  corridors: level2Corridors,
  start: sliceStart(level2Corridors[0]!, 50),
  goal: sliceFar(level2Corridors[level2Corridors.length - 1]!, 50, lastLegDirection(level2Points)),
  minWidth: 26,
};

// --- Level 3 "Storm": narrow, long zig-zag (~22px corridors) -----------------
// Unchanged from its prior geometry — already the hardest, most visually distinct
// shape of the three (the needle); nothing here should ever get easier.
const LEVEL3_WIDTH = 22;
const level3Points = [
  [40, 40],
  [40, 600],
  [160, 600],
  [160, 40],
  [280, 40],
  [280, 600],
  [400, 600],
  [400, 40],
  [520, 40],
  [520, 600],
  [640, 600],
  [640, 40],
  [760, 40],
  [760, 600],
  [880, 600],
  [880, 40],
] as const;
const level3Corridors = corridorPath(level3Points, LEVEL3_WIDTH);

const LEVEL3: Level = {
  id: 3,
  name: "Needle's Eye",
  theme: "storm",
  corridors: level3Corridors,
  start: sliceStart(level3Corridors[0]!, 40),
  // The final leg ([880,600] -> [880,40]) travels UPWARD — sliceFar (unlike the old
  // sliceEnd) places this goal at the corridor's TOP (y=40, the true dead end) instead
  // of its bottom (y=560..600, merely where the previous leg enters it).
  goal: sliceFar(level3Corridors[level3Corridors.length - 1]!, 40, lastLegDirection(level3Points)),
  minWidth: 18,
};

export const LEVELS: Level[] = [LEVEL1, LEVEL2, LEVEL3];

for (const level of LEVELS) {
  if (level.minWidth < MIN_WIDTH_FLOOR) {
    throw new Error(`levels: level ${level.id}'s minWidth below the cursor-safe floor`);
  }
  for (const r of level.corridors) {
    if (Math.min(r.w, r.h) < level.minWidth) {
      throw new Error(`levels: level ${level.id} has a corridor narrower than its own minWidth`);
    }
  }
}
